export interface Deck {
  id: number;
  discord_id: string;
  discord_username: string | null;
  name: string;
  deck_code: string;
  division_descriptor: string;
  is_public: boolean;
  tags: string | null;
  created_at: string;
  updated_at: string;
  isOutdated?: boolean;
  author_name?: string | null;
}

export interface DeckFilters {
  division_descriptor?: string;
  is_public?: boolean;
  user_id?: string;
  page?: number;
  limit?: number;
  search?: string;
  tags?: string; // Comma-separated tags to filter by
}

export interface PaginatedDecksResponse {
  data: Deck[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
}

export interface CreateDeckData {
  deck_code: string;
  division_descriptor: string;
  division_name?: string; // For non-verified users to format name
  name?: string; // For verified users
  is_public: boolean;
  tags?: string;
}

export interface UpdateDeckData {
  deck_code?: string;
  division_descriptor?: string;
  name?: string;
  is_public?: boolean;
  tags?: string;
}

/**
 * Fetch list of decks with optional filters and pagination
 */
export async function fetchDecks(filters: DeckFilters = {}): Promise<PaginatedDecksResponse> {
  const params = new URLSearchParams();
  
  if (filters.division_descriptor) {
    params.append("division_descriptor", filters.division_descriptor);
  }
  
  if (filters.is_public !== undefined) {
    params.append("is_public", filters.is_public.toString());
  }
  
  if (filters.user_id) {
    params.append("user_id", filters.user_id);
  }

  // Add pagination parameters
  if (filters.page !== undefined) {
    params.append("page", filters.page.toString());
  }

  if (filters.limit !== undefined) {
    params.append("limit", filters.limit.toString());
  }

  if (filters.search) {
    params.append("search", filters.search);
  }

  if (filters.tags) {
    params.append("tags", filters.tags);
  }

  const response = await fetch(`/api/decks?${params.toString()}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch decks: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch a single deck by ID
 */
export async function fetchDeck(id: number): Promise<Deck> {
  const response = await fetch(`/api/decks/${id}`);
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Deck not found");
    }
    throw new Error(`Failed to fetch deck: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new deck
 */
export async function createDeck(deckData: CreateDeckData): Promise<Deck> {
  const response = await fetch("/api/decks", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(deckData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to create deck: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing deck
 */
export async function updateDeck(id: number, deckData: UpdateDeckData): Promise<Deck> {
  const response = await fetch(`/api/decks/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(deckData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to update deck: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a deck
 */
export async function deleteDeck(id: number): Promise<void> {
  const response = await fetch(`/api/decks/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to delete deck: ${response.statusText}`);
  }
}

/**
 * Fetch all decks for admin view
 */
export async function fetchAdminDecks(): Promise<Deck[]> {
  const response = await fetch("/api/admin/decks", {
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    throw new Error(`Failed to fetch admin decks: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a deck as admin
 */
export async function deleteAdminDeck(id: number): Promise<void> {
  const response = await fetch(`/api/admin/decks/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to delete deck: ${response.statusText}`);
  }
}


